/**
 * Native shell file-drop bridge — payload shape + pure converters.
 *
 * On macOS the desktop shell owns OS file drags (WKWebView won't hand Finder file
 * CONTENTS to HTML5 `dataTransfer.files`), so the Rust side (`file_drop.rs`) reads the
 * dropped bytes and dispatches a `vestra:file-drop` CustomEvent carrying base64. These
 * helpers turn that payload back into `File` objects for the existing attach lane.
 * Inert in a plain browser, where HTML5 drag-drop works directly and these never fire.
 */

export interface NativeDropFile {
  name: string;
  mime: string;
  dataBase64: string;
}

export interface NativeDropError {
  name: string;
  reason: "too_large" | "unreadable" | string;
  sizeBytes?: number;
}

export interface NativeDropDetail {
  files?: NativeDropFile[];
  errors?: NativeDropError[];
  /** Drop point in PHYSICAL pixels (scale by devicePixelRatio for CSS px). */
  x: number;
  y: number;
}

/** Decode a base64 string to raw bytes (browser `atob`, no Node Buffer). The
 *  `<ArrayBuffer>` param keeps the result a valid `BlobPart` for `new File(...)`. */
export function base64ToBytes(base64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** Convert native drop entries into `File` objects for the attach lane. */
export function entriesToFiles(entries: NativeDropFile[]): File[] {
  return entries.map(
    (entry) =>
      new File([base64ToBytes(entry.dataBase64)], entry.name, {
        type: entry.mime,
      })
  );
}

/** True when the CSS-pixel point falls inside the rect (composer hit-test). */
export function pointInRect(
  x: number,
  y: number,
  rect: DOMRect | null | undefined
): boolean {
  if (!rect) return false;
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}
